import { Component, OnInit } from '@angular/core';
import { Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@core/services/auth.service';
import { BBIDService } from '@core/services/bbid.service';
import { PortalDeterminationService } from '@core/services/portal-determination.service';
import { SpinnerService } from '@core/services/spinner.service';
import { TokenService } from '@core/services/token/token.service';
import { BbidLinkFailedReason } from '@core/typings/auth.typing';
import { TypeSafeFormBuilder, TypeSafeFormGroup } from '@yourcause/common/core-forms';
import { I18nService } from '@yourcause/common/i18n';
import { PanelTypes } from '@yourcause/common/panel';

interface VerificationCodeFormGroup {
  code: string;
}
@Component({
  selector: 'gc-verification-code-page',
  templateUrl: './verification-code-page.component.html',
  styleUrls: ['./verification-code-page.component.scss']
})
export class VerificationCodePageComponent implements OnInit {
  PanelTypes = PanelTypes;
  formGroup: TypeSafeFormGroup<VerificationCodeFormGroup>;
  username: string = this.authService.username || this.activatedRoute.snapshot.queryParams.email;
  initialRoute = this.activatedRoute.snapshot.queryParams.initialRoute;
  grantProgramGuid = this.activatedRoute.snapshot.queryParams.grantProgramGuid;
  clientId = this.activatedRoute.snapshot.queryParams.clientId;
  isBbid = this.activatedRoute.snapshot.queryParams.isBbid === 'true';
  inSystem = this.activatedRoute.snapshot.queryParams.inSystem === 'true';
  loading: boolean;
  codeInvalid = false;
  codeInvalidText = this.i18n.translate(
    'login:textTheCodeEnteredIsNotValid',
    {},
    'The code you entered is not valid.'
  );
  bbidTiedToAnotherAccount = false;
  bbidEmail: string;
  failReason: BbidLinkFailedReason.BBID_LINKED_TO_ANOTHER_ACCOUNT|BbidLinkFailedReason.BBID_NON_LINKED_EMAIL_EXISTS_IN_GC;

  constructor (
    private formBuilder: TypeSafeFormBuilder,
    private i18n: I18nService,
    private spinnerService: SpinnerService,
    private authService: AuthService,
    private router: Router,
    private portal: PortalDeterminationService,
    private activatedRoute: ActivatedRoute,
    private bbidService: BBIDService,
    private tokenService: TokenService
  ) { }

  async ngOnInit () {
    if (!this.isBbid) {
      this.router.navigate(['../'], {
        relativeTo: this.activatedRoute
      });
    }
    this.spinnerService.startSpinner();
    await this.resendSecurityCode(false);
    this.spinnerService.stopSpinner();
    this.formGroup = this.formBuilder.group<VerificationCodeFormGroup>({
      code: ['', Validators.required]
    });

    this.handleSubmit = this.handleSubmit.bind(this);
  }

  async resendSecurityCode (isResend = true) {
    this.spinnerService.startSpinner();
    await this.authService.sendVerificationCode(
      this.username,
      this.portal.getUserType(),
      isResend,
      this.clientId
    );
    this.spinnerService.stopSpinner();
  }

  async handleSubmit () {
    this.spinnerService.startSpinner();
    this.codeInvalid = false;
    this.bbidTiedToAnotherAccount = false;
    const {
      failReason
    } = await this.authService.handleLinkingBbidAccount(
      true,
      this.clientId,
      this.username,
      this.formGroup.value.code,
      this.grantProgramGuid
    );
    if (failReason === BbidLinkFailedReason.BBID_LINKED_TO_ANOTHER_ACCOUNT) {
      const token = await this.bbidService.getToken();
      const parsed = this.tokenService.parseJwt(token);
      this.bbidEmail = parsed.email;
      this.bbidTiedToAnotherAccount = true;
      this.failReason = failReason;
    } else if (failReason === BbidLinkFailedReason.INVALID_MFA_DIFFERENT_EMAILS) {
      this.codeInvalid = true;
    }
    this.spinnerService.stopSpinner();
  }
}
